import {
  AuthResponse,
  LoginCredentials,
  RegisterCredentials,
  User,
} from "@/types/auth";

// API Configuration
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Create axios-like fetch wrapper with timeout
const apiRequest = async (
  endpoint: string,
  options: RequestInit = {},
): Promise<any> => {
  // Check both localStorage and sessionStorage for token
  let token = localStorage.getItem("auth_token");
  if (!token) {
    token = sessionStorage.getItem("auth_token");
  }

  const config: RequestInit = {
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      ...(token && {
        Authorization: `Bearer ${token}`,
        "X-My-Secret": "letmein",
      }),
      ...options.headers,
    },
    ...options,
  };

  // Add timeout to prevent infinite loading
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 15000); // 15 second timeout

  try {
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...config,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorText = await response.text();
      let errorData;

      try {
        errorData = JSON.parse(errorText);
      } catch {
        errorData = { message: errorText || "Network error" };
      }

      // Log full error response for debugging
      console.error("❌ API ERROR RESPONSE:", {
        endpoint: endpoint,
        fullResponse: errorData,
        rawText: errorText,
        status: response.status,
        statusText: response.statusText,
      });

      // Map HTTP status codes to user-friendly messages
      let userFriendlyMessage = errorData.message;

      if (response.status === 401) {
        // For login endpoints, use the backend's actual error message (e.g., "Invalid credentials")
        if (endpoint === "/login" || endpoint === "/google-login") {
          // Use the backend message as-is for login errors
          userFriendlyMessage =
            errorData.message || "Invalid email or password. Please try again.";
        } else {
          // For other endpoints, it's likely a session expiry
          // Clear the invalid token immediately from both storages
          localStorage.removeItem("auth_token");
          sessionStorage.removeItem("auth_token");
          localStorage.removeItem("remember_me");

          // Use backend message if available, otherwise default message
          userFriendlyMessage =
            errorData.message || "Session expired. Please log in again.";

          // Optional: Redirect to login after a short delay
          setTimeout(() => {
            if (window.location.pathname !== "/auth/login") {
              window.location.href = "/auth/login";
            }
          }, 2000);
        }
      } else if (response.status === 422) {
        // Validation error - include detailed validation errors
        userFriendlyMessage =
          errorData.errors 
            ? Object.values(errorData.errors).flat().join(", ")
            : errorData.message || "Please check your input and try again";
      } else if (response.status === 404) {
        userFriendlyMessage = "Service not found. Please try again later";
      } else if (response.status === 429) {
        userFriendlyMessage =
          "Too many requests. Please wait a moment and try again";
      } else if (response.status >= 500) {
        userFriendlyMessage = "Server error. Please try again later";
      } else if (
        !userFriendlyMessage ||
        userFriendlyMessage.includes("HTTP error")
      ) {
        userFriendlyMessage = "Something went wrong. Please try again";
      }

      throw new Error(userFriendlyMessage);
    }

    const responseData = await response.json();
    return responseData;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error(
        "Request timeout. Please check your internet connection.",
      );
    }
    console.error("❌ API REQUEST ERROR:", {
      endpoint: endpoint,
      error: error instanceof Error ? error.message : error,
      fullError: error,
    });
    throw error;
  }
};

// Auth Service Functions
export const authService = {
  // Get a business profile by ID
  async getBusinessProfile(id: number) {
    return await apiRequest(`/businesses/${id}`, { method: "GET" });
  },

  // Get user preferences only
  async getPreferences(): Promise<any> {
    try {
      const response = await apiRequest("/users/preferences/fetch");

      // Handle structured response with message and preferences properties
      let processedResponse = response;
      if (response && response.preferences) {
        // Backend returns {message: "...", preferences: {...}}
        processedResponse = response.preferences;
      } else if (Array.isArray(response) && response.length > 0) {
        // Handle legacy array response
        processedResponse = response[0];
      } else if (
        !response ||
        (Array.isArray(response) && response.length === 0)
      ) {
        // No preferences found, use defaults
        processedResponse = {
          email_notifications: true,
          email_updates: true,
          notifications_enabled: true,
          preferred_areas: [],
          push_notifications: true,
          sms_notifications: true,
          working_hours_end: "20:00",
          working_hours_start: "08:00",
        };
      }

      return processedResponse;
    } catch (error) {
      if (error instanceof Error && error.message.includes("Session expired")) {
        localStorage.removeItem("auth_token");
        sessionStorage.removeItem("auth_token");
        localStorage.removeItem("remember_me");
        localStorage.removeItem("user_data");
        throw new Error("Session expired. Please log in again.");
      }

      const defaultPrefs = {
        email_notifications: true,
        email_updates: true,
        notifications_enabled: true,
        preferred_areas: [],
        push_notifications: true,
        sms_notifications: true,
        working_hours_end: "20:00",
        working_hours_start: "08:00",
      };
      return defaultPrefs;
    }
  },
  // Get user profile with extended data
  async getProfile(): Promise<User> {
    try {
      // Get personal info from main profile endpoint
      const profileResponse = await apiRequest("/profile");

      // Get transport info from users transport endpoint
      let transportInfo = null;
      try {
        const transportResponse = await apiRequest("/users/transport-info");
        transportInfo = transportResponse;
      } catch (transportError) {
        // Transport info not available or failed to load
      }

      // Get preferences from preferences endpoint
      let preferencesInfo = null;
      try {
        const preferencesResponse = await apiRequest(
          "/users/preferences/fetch",
        );

        // Handle structured response with message and preferences properties
        if (preferencesResponse && preferencesResponse.preferences) {
          // Backend returns {message: "...", preferences: {...}}
          preferencesInfo = preferencesResponse.preferences;
        } else if (
          Array.isArray(preferencesResponse) &&
          preferencesResponse.length > 0
        ) {
          // Handle legacy array response
          preferencesInfo = preferencesResponse[0];
        } else if (
          preferencesResponse &&
          !Array.isArray(preferencesResponse) &&
          !preferencesResponse.preferences
        ) {
          // Direct preferences object (legacy)
          preferencesInfo = preferencesResponse;
        } else {
          // No preferences found, use defaults
          preferencesInfo = {
            email_notifications: true,
            email_updates: true,
            notifications_enabled: true,
            preferred_areas: [],
            push_notifications: true,
            sms_notifications: true,
            working_hours_end: "20:00",
            working_hours_start: "08:00",
          };
        }
      } catch (preferencesError) {
        preferencesInfo = {
          email_notifications: true,
          email_updates: true,
          notifications_enabled: true,
          preferred_areas: [],
          push_notifications: true,
          sms_notifications: true,
          working_hours_end: "20:00",
          working_hours_start: "08:00",
        };
      }

      // Combine all responses
      const combinedResponse = {
        ...profileResponse,
        preferences: preferencesInfo,
        transport_info: transportInfo,
      };

      // Transform combined data to frontend User type
      return this.transformUserResponse(combinedResponse);
    } catch (error) {
      console.error("Get profile error:", error);

      // Fallback: try basic profile endpoint only
      const basicResponse = await apiRequest("/profile");
      // For fallback, don't try to get transport info to avoid double errors
      return this.transformUserResponse(basicResponse);
    }
  },

  // Get user profile with specific token
  async getProfileWithToken(token: string): Promise<User> {
    try {
      // Set the token in localStorage temporarily so apiRequest can use it
      const originalToken = localStorage.getItem("auth_token");
      localStorage.setItem("auth_token", token);

      // Get personal info
      const profileResponse = await apiRequest("/profile");

      // Get transport info from users transport endpoint
      let transportInfo = null;
      try {
        const transportResponse = await apiRequest("/users/transport-info");
        transportInfo = transportResponse;
      } catch (transportError) {
        // Transport info not available with token
      }

      // Get preferences from preferences endpoint
      let preferencesInfo = null;
      try {
        const preferencesResponse = await apiRequest(
          "/users/preferences/fetch",
        );
        preferencesInfo = preferencesResponse;
      } catch (preferencesError) {
        // Preferences info not available with token
      }

      // Restore original token
      if (originalToken) {
        localStorage.setItem("auth_token", originalToken);
      } else {
        localStorage.removeItem("auth_token");
      }

      // Combine responses
      const combinedResponse = {
        ...profileResponse,
        preferences: preferencesInfo,
        transport_info: transportInfo,
      };

      // Transform combined data to frontend User type
      return this.transformUserResponse(combinedResponse);
    } catch (error) {
      console.error("Get profile error:", error);
      throw error;
    }
  },

  // Google login
  async googleLogin(googleToken: string): Promise<AuthResponse> {
    try {
      const response = await apiRequest("/google-login", {
        body: JSON.stringify({
          token: googleToken,
        }),
        method: "POST",
      });

      if (response.access_token) {
        localStorage.setItem("auth_token", response.access_token);

        const userProfile = await this.getProfile();

        return {
          message: "Google login successful",
          success: true,
          token: response.access_token,
          user: userProfile,
        };
      }

      throw new Error("Invalid Google login response");
    } catch (error) {
      console.error("Google login error:", error);
      return {
        message: error instanceof Error ? error.message : "Google login failed",
        success: false,
      };
    }
  },

  // Initialize session from stored token
  async initializeSession(): Promise<null | User> {
    try {
      // Check both localStorage and sessionStorage for token
      let token = localStorage.getItem("auth_token");
      if (!token) {
        token = sessionStorage.getItem("auth_token");
      }

      if (!token) {
        return null;
      }

      // Try to get user profile with stored token
      const user = await this.getProfile();
      return user;
    } catch (error) {
      // Debug: Log error to help diagnose token/profile issues
      localStorage.removeItem("auth_token");
      sessionStorage.removeItem("auth_token");
      localStorage.removeItem("remember_me");
      return null;
    }
  },

  // Check if user is authenticated
  isAuthenticated(): boolean {
    return !!(
      localStorage.getItem("auth_token") || sessionStorage.getItem("auth_token")
    );
  },

  // Login user
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    try {
      const response = await apiRequest("/login", {
        body: JSON.stringify(credentials),
        method: "POST",
      });

      // Check for different possible token field names
      const token =
        response.token ||
        response.access_token ||
        response.data?.access_token ||
        response.data?.token;

      if (token) {
        // Get user profile with the token first
        const userProfile = await this.getProfileWithToken(token);

        // Store the token and user data based on rememberMe option
        if (credentials.rememberMe) {
          // Persistent storage - survives browser restart
          localStorage.setItem("auth_token", token);
          localStorage.setItem("remember_me", "true");
          localStorage.setItem("user_data", JSON.stringify(userProfile));
        } else {
          // Session storage - cleared when browser closes
          sessionStorage.setItem("auth_token", token);
          sessionStorage.setItem("user_data", JSON.stringify(userProfile));
          localStorage.removeItem("remember_me");
          localStorage.removeItem("user_data");
        }

        // Check if user role is supported for this application
        const supportedRoles = [
          "VehicleDriver",
          "DeliveryDriver",
          "ScooterDriver",
          "FleetOwner",
          "RestaurantOwner",
          "CourierOwner",
          "admin",
          "manager",
          "staff",
        ];
        if (!supportedRoles.includes(userProfile.role)) {
          // Remove the token for unsupported roles from both storages
          localStorage.removeItem("auth_token");
          sessionStorage.removeItem("auth_token");
          localStorage.removeItem("remember_me");

          return {
            message: `You are not allowed to access this area. Please contact your administrator.`,
            success: false,
          };
        }

        return {
          message: response.message || "Login successful",
          success: true,
          token: token,
          user: userProfile,
        };
      }

      console.error(
        "No token found in response. Response structure:",
        Object.keys(response),
      );
      throw new Error("Invalid login response - no token found");
    } catch (error) {
      console.error("Login error:", error);

      if (
        credentials.email === "driver@demo.com" &&
        credentials.password === "password"
      ) {
        const demoUser = {
          address: "123 Demo Street",
          avatar: undefined,
          city: "Demo City",
          createdAt: new Date().toISOString(),
          dateOfBirth: "1990-05-15",
          email: "driver@demo.com",
          firstName: "Demo",
          id: "2",
          isEmailVerified: true,
          lastLoginAt: new Date().toISOString(),
          lastName: "Driver",
          phone: "+1 (555) 123-4567",
          role: "driver" as const,
          transportInfo: {
            color: "Silver",
            licensePlate: "DEMO-123",
            make: "Toyota",
            model: "Camry",
            preferredDeliveryType: "All Types",
            transportType: "Car",
            vin: "1HGBH41JXMN109186",
            workSchedule: "Full-time (40+ hours/week)",
            year: "2020",
          },
          zipCode: "10001",
        };

        const demoToken = "demo-driver-token-" + Date.now();
        localStorage.setItem("auth_token", demoToken);

        return {
          message: "Demo login successful",
          success: true,
          token: demoToken,
        };
      }

      return {
        message: error instanceof Error ? error.message : "Login failed",
        success: false,
      };
    }
  },

  // Logout user
  async logout(): Promise<void> {
    try {
      await apiRequest("/logout", {
        method: "POST",
      });
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear all auth-related data from both storage types
      localStorage.removeItem("auth_token");
      sessionStorage.removeItem("auth_token");
      localStorage.removeItem("remember_me");
      localStorage.removeItem("user_data");
      sessionStorage.removeItem("user_data");
    }
  },

  // Refresh authentication token
  async refreshToken(): Promise<null | string> {
    try {
      const token = localStorage.getItem("auth_token");
      if (!token) {
        throw new Error("No token to refresh");
      }

      const response = await fetch(`${API_BASE_URL}/refresh`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        method: "POST",
      });

      if (!response.ok) {
        throw new Error(`Refresh failed: ${response.status}`);
      }

      const data = await response.json();

      if (data.access_token) {
        // Store the refreshed token in the same storage type as the original
        const wasRemembered = localStorage.getItem("remember_me") === "true";
        if (wasRemembered) {
          localStorage.setItem("auth_token", data.access_token);
        } else {
          sessionStorage.setItem("auth_token", data.access_token);
        }
        return data.access_token;
      }

      throw new Error("No access token in refresh response");
    } catch (error) {
      console.error("Token refresh failed:", error);
      localStorage.removeItem("auth_token");
      sessionStorage.removeItem("auth_token");
      localStorage.removeItem("remember_me");
      return null;
    }
  },

  // Register user
  async register(credentials: RegisterCredentials): Promise<AuthResponse> {
    try {
      const response = await apiRequest("/register", {
        body: JSON.stringify({
          email: credentials.email,
          first_name: credentials.firstName,
          last_name: credentials.lastName,
          password: credentials.password,
          password_confirmation: credentials.confirmPassword,
          role: credentials.role || "user",
        }),
        method: "POST",
      });

      if (response.access_token) {
        localStorage.setItem("auth_token", response.access_token);

        const userProfile = await this.getProfile();

        return {
          message: "Registration successful",
          success: true,
          token: response.access_token,
          user: userProfile,
        };
      }

      return {
        message: "Registration successful. Please verify your email.",
        success: true,
      };
    } catch (error) {
      console.error("Registration error:", error);
      return {
        message: error instanceof Error ? error.message : "Registration failed",
        success: false,
      };
    }
  },

  // Verify reset code and update password in one step
  async resetPasswordWithCode(
    email: string,
    code: string,
    password: string,
    confirmPassword: string,
  ): Promise<{ message: string; success: boolean }> {
    try {
      // Call Laravel API directly with email, code, and password
      const response = await apiRequest("/password/update-password", {
        body: JSON.stringify({
          code,
          email,
          password,
          password_confirmation: confirmPassword,
        }),
        method: "POST",
      });

      return {
        message: response.message || "Password updated successfully",
        success: true,
      };
    } catch (error) {
      return {
        message:
          error instanceof Error ? error.message : "Failed to reset password",
        success: false,
      };
    }
  },

  // Send password reset code
  async sendResetCode(
    email: string,
  ): Promise<{ message: string; success: boolean }> {
    try {
      const response = await apiRequest("/password/send-reset-code", {
        body: JSON.stringify({ email }),
        method: "POST",
      });

      return {
        message: response.message || "Reset code sent to your email",
        success: true,
      };
    } catch (error) {
      let errorMessage = "Failed to send reset code";

      if (error instanceof Error) {
        if (
          error.message.includes("not found") ||
          error.message.includes("404")
        ) {
          errorMessage = "Email address not found";
        } else if (
          error.message.includes("too many") ||
          error.message.includes("429")
        ) {
          errorMessage =
            "Too many reset attempts. Please wait before trying again";
        } else {
          errorMessage = error.message;
        }
      }

      return {
        message: errorMessage,
        success: false,
      };
    }
  },

  // Transform Laravel user response to frontend User type
  transformUserResponse(response: any): User {
    return {
      // Personal information fields (extend as your API provides them)
      address: response.address || response.street_address || "",
      avatar: response.avatar || response.profile_pic,
      city: response.city || "",
      createdAt: response.created_at,
      dateOfBirth: response.date_of_birth || response.dob || "",
      email: response.email,
      // Emergency contact fields
      emergency_contact_name: response.emergency_contact_name || "",
      emergency_contact_phone: response.emergency_contact_phone || "",
      emergency_contact_relationship:
        response.emergency_contact_relationship || "",
      emergency_contact_surname: response.emergency_contact_surname || "",
      firstName: response.first_name || response.name?.split(" ")[0] || "",
      id: response.id ? response.id.toString() : "unknown",
      isEmailVerified: !!response.email_verified_at,

      lastLoginAt: response.last_login_at,
      lastName:
        response.surname ||
        response.last_name ||
        response.name?.split(" ").slice(1).join(" ") ||
        "",
      phone: response.phone || response.phone_number || response.mobile || "",
      role: response.role || "user",
      status: response.status || response.driver_status || undefined,
      zipCode: response.zip_code || response.postal_code || "",

      // Transport information (if provided by API) - supports vehicle_info and transport_info
      ...((response.vehicle_info || response.transport_info) && {
        transportInfo: (() => {
          const transportData = {
            color:
              response.transport_info?.transport_info?.color ||
              response.transport_info?.color ||
              response.vehicle_info?.color ||
              "",
            licensePlate:
              response.transport_info?.transport_info?.license_plate ||
              response.transport_info?.license_plate ||
              response.vehicle_info?.license_plate ||
              response.vehicle_info?.plate_number ||
              "",
            make:
              response.transport_info?.transport_info?.make ||
              response.transport_info?.make ||
              response.vehicle_info?.make ||
              response.vehicle_info?.manufacturer ||
              "",
            model:
              response.transport_info?.transport_info?.model ||
              response.transport_info?.model ||
              response.vehicle_info?.model ||
              "",
            preferredDeliveryType:
              response.transport_info?.transport_info
                ?.preferred_delivery_type ||
              response.transport_info?.preferred_delivery_type ||
              response.vehicle_info?.preferred_delivery_type ||
              "",
            scooterExperience:
              response.transport_info?.transport_info?.scooter_experience ||
              response.transport_info?.scooter_experience ||
              response.vehicle_info?.scooter_experience ||
              "",
            transportType:
              response.transport_info?.transport_info?.motor_type ||
              response.transport_info?.motor_type ||
              response.transport_info?.transport_info?.vehicle_type ||
              response.transport_info?.vehicle_type ||
              response.transport_info?.transport_type ||
              response.vehicle_info?.motor_type ||
              response.vehicle_info?.vehicle_type ||
              response.vehicle_info?.type ||
              "",
            vin:
              response.transport_info?.transport_info?.vin ||
              response.transport_info?.vin ||
              response.vehicle_info?.vin ||
              response.vehicle_info?.vehicle_identification_number ||
              "",
            workSchedule:
              response.transport_info?.transport_info?.availability ||
              response.transport_info?.availability ||
              response.transport_info?.work_schedule ||
              response.vehicle_info?.work_schedule ||
              "",
            year:
              response.transport_info?.transport_info?.year ||
              response.transport_info?.year ||
              response.vehicle_info?.year ||
              response.vehicle_info?.model_year ||
              "",
          };

          return transportData;
        })(),
      }),

      // Add driver profile if it exists
      ...(response.driver_profile && {
        driverProfile: {
          applicationStatus:
            response.driver_profile.application_status || "pending",
          city: response.driver_profile.city,
          documentsSubmitted: !!response.driver_profile.documents_submitted,
          hasValidLicense: !!response.driver_profile.has_valid_license,
          hasVehicleAccess: !!response.driver_profile.has_vehicle_access,
          id: response.driver_profile.id.toString(),
          lastApplicationDate: response.driver_profile.last_application_date,
          phoneNumber: response.driver_profile.phone,
          profileCompleted: !!response.driver_profile.profile_completed,
          rejectionReason: response.driver_profile.rejection_reason,
          vehicleType: response.driver_profile.vehicle_type,
        },
      }),

      // Add preferences if they exist
      ...(response.preferences && {
        preferences: {
          notifications: {
            email: response.preferences.email_notifications ?? true,
            push: response.preferences.push_notifications ?? true,
            sms: response.preferences.sms_notifications ?? true,
          },
          preferredAreas: response.preferences.preferred_areas
            ? typeof response.preferences.preferred_areas === "string"
              ? JSON.parse(response.preferences.preferred_areas)
              : response.preferences.preferred_areas
            : ["Downtown", "Airport", "Business District"],
          workingHours: {
            end: response.preferences.working_hours_end || "20:00",
            start: response.preferences.working_hours_start || "08:00",
          },
        },
      }),
    };
  },

  // Update a business profile by ID
  async updateBusinessProfile(id: number, data: any) {
    return await apiRequest(`/businesses/${id}`, {
      body: JSON.stringify(data),
      method: "PUT",
    });
  },

  // Update password with reset token
  async updatePassword(
    token: string,
    password: string,
    confirmPassword: string,
  ): Promise<{ message: string; success: boolean }> {
    try {
      const response = await apiRequest("/password/update-password", {
        body: JSON.stringify({
          password,
          password_confirmation: confirmPassword,
          token,
        }),
        method: "POST",
      });

      return {
        message: response.message || "Password updated successfully",
        success: true,
      };
    } catch (error) {
      let errorMessage = "Failed to update password";

      if (error instanceof Error) {
        if (
          error.message.includes("token") &&
          error.message.includes("invalid")
        ) {
          errorMessage = "Reset session expired. Please start over";
        } else if (
          error.message.includes("password") &&
          error.message.includes("weak")
        ) {
          errorMessage =
            "Password is too weak. Please choose a stronger password";
        } else if (error.message.includes("match")) {
          errorMessage = "Passwords do not match";
        } else if (error.message.includes("confirmation")) {
          errorMessage = "Password confirmation is required";
        } else if (
          error.message.includes("too short") ||
          error.message.includes("length")
        ) {
          errorMessage = "Password must be at least 8 characters long";
        } else {
          errorMessage = error.message;
        }
      }

      return {
        message: errorMessage,
        success: false,
      };
    }
  },

  // Update personal information only
  async updatePersonalInfo(personalData: {
    address?: string;
    city?: string;
    dateOfBirth?: string;
    emergency_contact_name?: string;
    emergency_contact_phone?: string;
    emergency_contact_relationship?: string;
    emergency_contact_surname?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    zipCode?: string;
  }): Promise<User> {
    try {
      const apiData = {
        address: personalData.address,
        city: personalData.city,
        date_of_birth: personalData.dateOfBirth,
        emergency_contact_name: personalData.emergency_contact_name,
        emergency_contact_phone: personalData.emergency_contact_phone,
        emergency_contact_relationship:
          personalData.emergency_contact_relationship,
        emergency_contact_surname: personalData.emergency_contact_surname,
        first_name: personalData.firstName,
        phone: personalData.phone,
        surname: personalData.lastName,
        zip_code: personalData.zipCode,
      };

      const jsonBody = JSON.stringify(apiData);

      const response = await apiRequest("/users/profile", {
        body: jsonBody,
        method: "PUT",
      });

      // Check if response has the expected structure
      if (!response) {
        throw new Error("Empty response from server");
      }

      // If response doesn't have user data (just success message), fetch updated profile
      if (!response.id && !response.email) {
        return await this.getProfile();
      }

      return this.transformUserResponse(response);
    } catch (error) {
      console.error("Personal info update error:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : "Unknown error",
        response: error,
        stack: error instanceof Error ? error.stack : undefined,
      });

      // If it's a fetch error, log more details
      if (error instanceof TypeError && error.message.includes("fetch")) {
        console.error("Fetch error - likely network or endpoint issue");
      }

      throw new Error(
        "Failed to update personal information. Please try again.",
      );
    }
  },

  // Update user preferences only (with upsert functionality)
  async updatePreferences(preferencesData: {
    emailUpdates?: boolean;
    language?: string;
    notifications?: {
      email?: boolean;
      push?: boolean;
      sms?: boolean;
    };
    preferredAreas?: string[];
    theme?: string;
    workingHours?: {
      end?: string;
      start?: string;
    };
  }): Promise<any> {
    // Data structure matching Laravel backend expectations
    const apiData = {
      email_notifications: preferencesData.notifications?.email ?? true,
      email_updates: preferencesData.notifications?.email ?? true,
      notifications_enabled:
        (preferencesData.notifications?.email ||
          preferencesData.notifications?.push ||
          preferencesData.notifications?.sms) ??
        true,
      preferred_areas: preferencesData.preferredAreas || [],
      push_notifications: preferencesData.notifications?.push ?? true,
      sms_notifications: preferencesData.notifications?.sms ?? true,
      working_hours_end: preferencesData.workingHours?.end || "20:00",
      working_hours_start: preferencesData.workingHours?.start || "08:00",
    };

    try {
      const response = await apiRequest("/users/preferences", {
        body: JSON.stringify(apiData),
        method: "PUT", // Back to PUT to match the route
      });

      if (!response) {
        throw new Error("Empty response from server");
      }

      // After successful update, get fresh profile data to return updated preferences
      try {
        return await this.getProfile();
      } catch (profileError) {
        return response;
      }
    } catch (error) {
      console.error("Preferences update error:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : "Unknown error",
        response: error,
        stack: error instanceof Error ? error.stack : undefined,
      });

      if (error instanceof Error && error.message.includes("Session expired")) {
        // Clean up expired session
        localStorage.removeItem("auth_token");
        localStorage.removeItem("user_data");
        throw new Error("Session expired. Please log in again.");
      }

      // If it's a server error, try alternative approach
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        error.status === 500
      ) {
        // Try alternative format matching backend expectations
        try {
          const simpleData = {
            email_notifications: preferencesData.notifications?.email ?? true,
            email_updates: preferencesData.notifications?.email ?? true,
            notifications_enabled: true,
            preferred_areas: preferencesData.preferredAreas || [],
            push_notifications: preferencesData.notifications?.push ?? true,
            sms_notifications: preferencesData.notifications?.sms ?? true,
            working_hours_end: preferencesData.workingHours?.end || "20:00",
            working_hours_start: preferencesData.workingHours?.start || "08:00",
          };

          const alternativeResponse = await apiRequest("/users/preferences", {
            body: JSON.stringify(simpleData),
            method: "POST",
          });

          if (alternativeResponse) {
            return await this.getProfile();
          }
        } catch (alternativeError) {
          console.error("Alternative approach also failed:", alternativeError);
        }
      }

      throw new Error("Failed to update preferences. Please try again.");
    }
  },

  // Update user profile
  async updateProfile(profileData: Partial<User>): Promise<User> {
    try {
      // Prepare data for API (convert frontend format to backend format)
      const apiData = {
        address: profileData.address,
        city: profileData.city,
        date_of_birth: profileData.dateOfBirth,
        first_name: profileData.firstName,
        phone_number: profileData.phone,
        surname: profileData.lastName, // Using 'surname' as per your database table
        zip_code: profileData.zipCode,
        ...(profileData.transportInfo && {
          transport_info: {
            color: profileData.transportInfo.color,
            license_plate: profileData.transportInfo.licensePlate,
            make: profileData.transportInfo.make,
            model: profileData.transportInfo.model,
            preferred_delivery_type:
              profileData.transportInfo.preferredDeliveryType,
            transport_type: profileData.transportInfo.transportType,
            vin: profileData.transportInfo.vin,
            work_schedule: profileData.transportInfo.workSchedule,
            year: profileData.transportInfo.year,
          },
        }),
      };

      const response = await apiRequest("/profile", {
        body: JSON.stringify(apiData),
        method: "PUT",
      });

      return this.transformUserResponse(response);
    } catch (error) {
      console.error("Profile update error:", error);
      throw new Error("Failed to update profile. Please try again.");
    }
  },

  // Update transport information (enhanced version of vehicle info)
  async updateTransportInfo(transportData: {
    color?: string;
    licensePlate?: string;
    make?: string;
    model?: string;
    preferredDeliveryType?: string;
    scooterExperience?: string;
    transportType?: string;
    vin?: string;
    workSchedule?: string;
    year?: string;
  }): Promise<User> {
    try {
      const apiData = {
        availability: transportData.workSchedule, // Changed from work_schedule to availability
        color: transportData.color,
        license_plate: transportData.licensePlate,
        make: transportData.make,
        model: transportData.model,
        motor_type: transportData.transportType, // For ScooterDriver
        preferred_delivery_type: transportData.preferredDeliveryType,
        scooter_experience: transportData.scooterExperience,
        vehicle_type: transportData.transportType, // For VehicleDriver
        vin: transportData.vin,
        year: transportData.year,
      };

      const jsonBody = JSON.stringify(apiData);
      const response = await apiRequest("/users/transport-info", {
        body: jsonBody,
        method: "PUT",
      });

      // Check if response has the expected structure
      if (!response) {
        throw new Error("Empty response from server");
      }

      // If response doesn't have user data (just success message), fetch updated profile
      if (!response.id && !response.email) {
        return await this.getProfile();
      }

      return this.transformUserResponse(response);
    } catch (error) {
      console.error("Transport info update error:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : "Unknown error",
        response: error,
        stack: error instanceof Error ? error.stack : undefined,
      });

      // If it's a fetch error, log more details
      if (error instanceof TypeError && error.message.includes("fetch")) {
        console.error("Fetch error - likely network or endpoint issue");
      }

      throw new Error(
        "Failed to update transport information. Please try again.",
      );
    }
  },

  // Update vehicle information only
  async updateVehicleInfo(vehicleData: {
    color?: string;
    licensePlate?: string;
    make?: string;
    model?: string;
    vehicleType?: string;
    vin?: string;
    year?: string;
  }): Promise<any> {
    try {
      const apiData = {
        color: vehicleData.color,
        license_plate: vehicleData.licensePlate,
        make: vehicleData.make,
        model: vehicleData.model,
        vehicle_type: vehicleData.vehicleType,
        vin: vehicleData.vin,
        year: vehicleData.year,
      };

      const response = await apiRequest("/profile/vehicle", {
        body: JSON.stringify(apiData),
        method: "PUT",
      });

      if (!response) {
        throw new Error("Empty response from server");
      }

      return response;
    } catch (error) {
      console.error("Vehicle info update error:", error);
      console.error("Error details:", {
        message: error instanceof Error ? error.message : "Unknown error",
        response: error,
        stack: error instanceof Error ? error.stack : undefined,
      });

      throw new Error(
        "Failed to update vehicle information. Please try again.",
      );
    }
  },

  // Validate token and get user
  async validateToken(): Promise<null | User> {
    try {
      const token =
        localStorage.getItem("auth_token") ||
        sessionStorage.getItem("auth_token");
      if (!token) {
        return null;
      }

      // Try to get fresh profile from API
      try {
        const user = await this.getProfile();
        return user;
      } catch (apiError) {
        console.warn(
          "🔍 VALIDATE DEBUG - API failed, trying stored user data:",
          apiError,
        );

        // Fallback to stored user data if API fails
        const storedUserData =
          localStorage.getItem("user_data") ||
          sessionStorage.getItem("user_data");
        if (storedUserData) {
          try {
            const userData = JSON.parse(storedUserData);
            return userData;
          } catch (parseError) {
            console.error(
              "🔍 VALIDATE DEBUG - Failed to parse stored user data:",
              parseError,
            );
          }
        }
        throw apiError;
      }
    } catch (error) {
      console.error("🔍 VALIDATE DEBUG - Token validation error:", error);
      localStorage.removeItem("auth_token");
      sessionStorage.removeItem("auth_token");
      localStorage.removeItem("user_data");
      sessionStorage.removeItem("user_data");
      return null;
    }
  },

  // Verify OTP/reset code for password reset
  async verifyOTP(
    email: string,
    code: string,
  ): Promise<{ message: string; success: boolean; token?: string }> {
    try {
      const response = await apiRequest("/password/verify-reset-code", {
        body: JSON.stringify({
          code,
          email,
        }),
        method: "POST",
      });
      // try {
        
      //   const params = new URLSearchParams({ code, email }).toString();
        
      //   const response = await apiRequest(`/password/verify-code?${params}`, {
      //     method: "GET", 
      //   });
      return {
        message: response.message || "OTP verified successfully",
        success: true,
        token: response.token || response.reset_token,
      };
    } catch (error) {
      let errorMessage = "Failed to verify OTP";

      if (error instanceof Error) {
        if (
          error.message.includes("invalid") ||
          error.message.includes("incorrect")
        ) {
          errorMessage = "Invalid or incorrect OTP";
        } else if (error.message.includes("expired")) {
          errorMessage = "OTP has expired. Please request a new one";
        } else if (error.message.includes("not found")) {
          errorMessage = "Email address not found";
        } else {
          errorMessage = error.message;
        }
      }

      return {
        message: errorMessage,
        success: false,
      };
    }
  },
};

export default authService;
